import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileEdit, RefreshCw, Pill, Package, AlertCircle, Thermometer, ShieldCheck, Barcode } from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";

const MedicineView = ({ id: propId, modal = false, onClose } = {}) => {
    const params = useParams();
    const navigate = useNavigate();
    const viewId = propId || params.id;
    const [medicine, setMedicine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchMedicine = async () => {
            if (!viewId) return;
            setLoading(true);
            try {
                const res = await axiosInstance.get(`/medicine/${viewId}`);
                setMedicine(res.data);
                setError("");
            } catch (err) {
                console.error("Error fetching medicine:", err);
                setError("Failed to load medicine.");
            } finally {
                setLoading(false);
            }
        };

        fetchMedicine();
    }, [viewId]);

    const InfoCard = ({ icon: Icon, label, value, highlight = false, className = '' }) => (
        <div className={`p-3 sm:p-4 rounded-xl border transition-all hover:shadow-md ${highlight
            ? className || 'bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200'
            : 'bg-white border-gray-200'
            }`}>
            <div className="flex items-start gap-2 sm:gap-3">
                <div className={`p-1.5 sm:p-2 rounded-lg ${highlight
                    ? className.includes('blue') ? 'bg-blue-100'
                        : className.includes('purple') ? 'bg-purple-100'
                            : className.includes('orange') ? 'bg-orange-100'
                                : 'bg-teal-100'
                    : 'bg-gray-100'
                    }`}>
                    <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${highlight
                        ? className.includes('blue') ? 'text-blue-600'
                            : className.includes('purple') ? 'text-purple-600'
                                : className.includes('orange') ? 'text-orange-600'
                                    : 'text-teal-600'
                        : 'text-gray-600'
                        }`} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${highlight
                        ? className.includes('blue') ? 'text-blue-500'
                            : className.includes('purple') ? 'text-purple-500'
                                : className.includes('orange') ? 'text-orange-500'
                                    : 'text-teal-500'
                        : 'text-gray-500'
                        }`}>{label}</p>
                    <p className="text-sm font-semibold text-gray-900 break-words">{value || '-'}</p>
                </div>
            </div>
        </div>
    );

    const Badge = ({ children, color = "gray" }) => {
        const colors = {
            teal: "bg-teal-100 text-teal-700 border-teal-200",
            red: "bg-red-100 text-red-700 border-red-200",
            blue: "bg-blue-100 text-blue-700 border-blue-200",
            gray: "bg-gray-100 text-gray-700 border-gray-200"
        };
        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${colors[color]}`}>
                {children}
            </span>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 animate-pulse">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-2xl"></div>
                        <div className="flex-1 space-y-2 w-full">
                            <div className="h-7 sm:h-8 bg-gray-200 rounded-lg w-2/3 sm:w-1/3"></div>
                            <div className="h-4 bg-gray-100 rounded w-1/2 sm:w-1/4"></div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-20 sm:h-24 bg-gray-100 rounded-xl"></div>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
                {/* Header Section - Responsive */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                        <button
                            onClick={() => (modal ? onClose?.() : navigate(-1))}
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-3 sm:mb-4 group text-sm sm:text-base"
                        >
                            <div className="p-1 rounded-lg group-hover:bg-gray-100 transition-colors">
                                <ArrowLeft className="w-4 h-4" />
                            </div>
                            <span className="font-medium">Back</span>
                        </button>

                        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                            <div className="p-2.5 sm:p-3 bg-teal-600 rounded-2xl shadow-lg flex-shrink-0">
                                <Pill className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 break-words">
                                    {medicine?.name}
                                </h1>
                                <p className="text-sm sm:text-base text-gray-600 font-medium">{medicine?.generic_name}</p>
                                <div className="flex flex-wrap gap-2 mt-2 sm:mt-3">
                                    {medicine?.category && <Badge color="teal">{medicine.category}</Badge>}
                                    {medicine?.is_prescription_required && (
                                        <Badge color="red">
                                            <ShieldCheck className="w-3 h-3 mr-1 inline" />
                                            Prescription
                                        </Badge>
                                    )}
                                    {medicine?.is_refrigerated && (
                                        <Badge color="blue">
                                            <Thermometer className="w-3 h-3 mr-1 inline" />
                                            Cold Storage
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Info Cards - Fully Responsive */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <InfoCard
                        icon={Pill}
                        label="Strength"
                        value={medicine?.strength}
                        highlight
                        className="from-teal-50 to-cyan-50 border-teal-200"
                    />
                    <InfoCard
                        icon={Package}
                        label="UOM"
                        value={medicine?.uom}
                        highlight
                        className="from-blue-50 to-indigo-50 border-blue-200"
                    />
                    <InfoCard
                        icon={Package}
                        label="Units/Pack"
                        value={medicine?.units_per_package}
                        highlight
                        className="from-purple-50 to-pink-50 border-purple-200"
                    />
                    <InfoCard
                        icon={Barcode}
                        label="HSN Code"
                        value={medicine?.hsn_code}
                        highlight
                        className="from-orange-50 to-amber-50 border-orange-200"
                    />
                </div>

                {/* Main Details Section */}
                <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-100 overflow-hidden">
                    <div className="bg-teal-600 px-4 sm:px-6 py-3 sm:py-4">
                        <h2 className="text-base sm:text-lg font-bold text-white">Medicine Details</h2>
                    </div>

                    <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        {[
                            { label: "Manufacturer", value: medicine?.manufacturer },
                            { label: "GST %", value: medicine?.gst_percentage ? `${medicine.gst_percentage}%` : '-' },
                            { label: "Rack No.", value: medicine?.rack_number },
                            { label: "Barcode", value: medicine?.bar_code, mono: true },
                            { label: "Reorder Level", value: medicine?.reorder_level ?? '-' },
                            { label: "Max Stock", value: medicine?.max_stock_level ?? '-' },
                            { label: "Prescription", value: medicine?.is_prescription_required ? 'Yes' : 'No' },
                            { label: "Refrigerated", value: medicine?.is_refrigerated ? 'Yes' : 'No' },
                        ].map((item, i) => (
                            <div key={i} className="space-y-1">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                                <p className={`text-gray-900 font-medium ${item.mono ? 'font-mono text-sm' : ''}`}>
                                    {item.value || '-'}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Description & Composition - Responsive Grid */}
                    {(medicine?.description || medicine?.composition) && (
                        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                            {medicine?.description && (
                                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-3 sm:p-4 rounded-xl border border-blue-100">
                                    <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">Description</p>
                                    <p className="text-sm sm:text-base text-gray-800 leading-relaxed">{medicine.description}</p>
                                </div>
                            )}
                            {medicine?.composition && (
                                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 sm:p-4 rounded-xl border border-purple-100">
                                    <p className="text-xs font-semibold text-purple-900 uppercase tracking-wide mb-2">Composition</p>
                                    <p className="text-sm sm:text-base text-gray-800 whitespace-pre-wrap leading-relaxed">
                                        {medicine.composition}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Modal Close Button */}
                {modal && (
                    <div className="mt-5 sm:mt-6 flex justify-center sm:justify-end">
                        <button
                            onClick={() => onClose?.()}
                            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm sm:text-base font-medium"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        );
    };

    if (modal) {
        return (
            <div className="bg-gray-50 min-h-screen">
                {renderContent()}
            </div>
        );
    }

    return (
        <Authenticated>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {renderContent()}
            </div>
        </Authenticated>
    );
};

export default MedicineView;